import React, { useState, useEffect } from "react";
import { useGetRelanceFacture } from "../services/RelanceFactureService";
import * as LoadingIcons from "react-loading-icons";
import { ToastContainer } from "react-toastify";
import { FaUser, FaEnvelope, FaPhone, FaFileInvoice } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import RelanceFacturePopup from "./RelanceFacturePopup";

const RelanceFactureList = () => {
    const { relances, loading, pagination, page, setPage, fetchRelances } = useGetRelanceFacture();
    const [searchTerm, setSearchTerm] = useState("");
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedFacture, setSelectedFacture] = useState(null);

    useEffect(() => {
        fetchRelances(page);
    }, [page, fetchRelances]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) setPage(newPage);
    };

    const handleOpenPopup = (facture) => {
        setSelectedFacture(facture);
        setOpenPopup(true);
    };

    // Filtrer directement les relances par nom de client
    const filteredRelances = relances.filter((r) =>
        r.facture?.client?.nom_affichage?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <section className="pb-4 px-4 w-full">
            <ToastContainer position="top-right" autoClose={3000} />

            {openPopup && selectedFacture && (
                <RelanceFacturePopup
                    isOpen={openPopup}
                    onClose={() => setOpenPopup(false)}
                    facture={selectedFacture}
                    defaultMessage={`Bonjour ${selectedFacture?.client?.nom_affichage},\nNous attendons toujours le règlement de la facture ${selectedFacture?.numero}.`}
                    onSuccess={() => fetchRelances(page)}
                />
            )}

            {/* Header + recherche */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                <h1 className="font-bold text-2xl text-color1">
                    Liste des Relances Factures
                </h1>
                <input
                    type="text"
                    placeholder="Rechercher un client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border border-color10 rounded-lg px-3 py-2 w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-color3"
                />
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-[400px]">
                    <LoadingIcons.SpinningCircles stroke="#1A2A6C" height={40} width={40} />
                </div>
            ) : filteredRelances.length === 0 ? (
                <p className="text-center text-color6 mt-4">
                    Aucune relance facture trouvée.
                </p>
            ) : (
                <div className="flex flex-col gap-6">
                    {filteredRelances.map((r) => (
                        <div
                            key={r.facture?.id}
                            className="bg-color2 border border-color13 rounded-xl shadow-sm hover:shadow-md transition p-5 flex flex-col gap-6"
                        >
                            {/* Header facture + client */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start border-b pb-4">
                                {/* Infos facture */}
                                <div className="flex flex-col gap-1">
                                    <h3 className="font-extrabold flex items-center gap-2 text-color1">
                                        <FaFileInvoice /> Facture
                                    </h3>
                                    <p className="text-color6 text-sm">N° : {r.facture?.numero || "-"}</p>
                                    <p className="text-color6 text-sm">
                                        Montant : {(r.facture?.total_centimes / 100).toFixed(2)} {r.facture?.devise || "EUR"}
                                    </p>
                                    <p className="text-color6 text-sm">Statut : {r.facture?.statut || "-"}</p>
                                </div>

                                {/* Infos client */}
                                <div className="flex flex-col gap-1">
                                    <h3 className="font-extrabold flex items-center gap-2 text-color15">
                                        <FaUser /> Client
                                    </h3>
                                    <p className="text-color6 text-sm">{r.facture?.client?.nom_affichage || "-"}</p>
                                    {r.facture?.client?.email && (
                                        <p className="text-sm text-color5 flex items-center">
                                            <FaEnvelope className="mr-2" /> {r.facture.client.email}
                                        </p>
                                    )}
                                    {r.facture?.client?.telephone && (
                                        <p className="text-sm text-color5 flex items-center">
                                            <FaPhone className="mr-2" /> {r.facture.client.telephone}
                                        </p>
                                    )}
                                </div>

                                {/* Bouton relancer */}
                                <div className="flex md:justify-end items-start">
                                    <button
                                        onClick={() => handleOpenPopup(r.facture)}
                                        className="px-4 py-2 bg-color1 hover:bg-color3 text-color2 rounded-lg text-sm font-medium transition"
                                    >
                                        Relancer encore
                                    </button>
                                </div>
                            </div>

                            {/* Liste des relances */}
                            <div className="flex flex-col gap-4">
                                <h3 className="font-medium text-color1">Historique des relances</h3>
                                <div className="flex flex-col gap-3 max-h-48 overflow-y-auto pr-2">
                                    {r.relances.length > 0 ? (
                                        r.relances.map((rel) => (
                                            <div
                                                key={rel.id}
                                                className="border rounded-lg p-3 bg-color18 flex flex-col gap-1"
                                            >
                                                {/* Titre / Sujet */}
                                                <p className="font-semibold text-color3 text-sm">
                                                    {rel.subject}
                                                </p>

                                                {/* Message */}
                                                <p className="text-color6 text-sm">{rel.message}</p>

                                                {/* Date */}
                                                <p className="text-xs text-color6/80 mt-1">
                                                    {formatDistanceToNow(new Date(rel.date_relance), {
                                                        addSuffix: true,
                                                        locale: fr,
                                                    })}
                                                </p>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-color4 italic">
                                            Aucune relance pour cette facture.
                                        </p>
                                    )}
                                </div>
                            </div>


                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            <div className="flex justify-center items-center gap-3 mt-4">
                <button
                    disabled={pagination.current_page === 1}
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    className={`px-4 py-1 rounded-lg font-semibold text-sm ${pagination.current_page === 1 ? "bg-color10 text-color11 cursor-not-allowed" : "bg-color1 text-color2 hover:bg-color3 cursor-pointer"
                        }`}
                >
                    Précédent
                </button>
                <span className="text-color6 font-medium text-sm">
                    {pagination.current_page} / {pagination.last_page}
                </span>
                <button
                    disabled={pagination.current_page === pagination.last_page}
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    className={`px-4 py-1 rounded-lg font-semibold text-sm ${pagination.current_page === pagination.last_page ? "bg-color10 text-color11 cursor-not-allowed" : "bg-color1 text-color2 hover:bg-color3 cursor-pointer"
                        }`}
                >
                    Suivant
                </button>
            </div>
        </section>
    );
};

export default RelanceFactureList;
